import {CloseOutlined, ExportOutlined, PlusOutlined, QuestionCircleFilled} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    Divider,
    Flex,
    Form,
    GetProp,
    Image,
    Input,
    InputNumber,
    List,
    Row,
    Select,
    Space,
    Spin,
    Tooltip,
    Typography,
    Upload,
    UploadFile,
    UploadProps,
} from 'antd';
import {RcFile} from 'antd/es/upload/interface';
import {UploadChangeParam} from 'antd/lib/upload';
import dayjs from 'dayjs';
import React, {useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';

import {ApiQuery, ProductsApiPostAdminAttachImagesRequest} from '../../../gen/api';
import {useAdminPalletOrderLookupMutation} from '../../../gen/api/apis';
import {AdminMarketplaceType, AdminPalletOrderLookupItem, AdminProductStatus} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {marketplaceLabels, productConsistencies, productStatuses} from '../../helpers/labels';
import styles from './create_product.module.less';

const {Text} = Typography;

type Marketplace = {
    name: AdminMarketplaceType;
    link: string;
    price: number;
};

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

export const ProductCreatePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [form] = Form.useForm();
    const asin = Form.useWatch('asin', form);

    const cloneId = searchParams.get('clone') as string | undefined;
    const [loadingClone, setLoadingClone] = React.useState(false);

    const notify = useNotification();

    const [error, setError] = React.useState<string | null>(null);
    const marketplaces = Form.useWatch('marketplaces', form);

    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewImage, setPreviewImage] = React.useState('');
    const [fileList, setFileList] = React.useState<UploadFile[]>([]);
    const [createLoading, setCreateLoading] = React.useState(false);

    const fetchToClone = ApiQuery.useGetAdminProductDetailMutation({
        onSuccess: (data) => {
            form.setFieldsValue({
                name: data.name,
                description: data.description,
                msrp: data.mSRP,
                category: data.category.id,
                status: AdminProductStatus.ProductStatusReadyForSale,
                consistency: data.consistency,
                consistency_details: data.consistencyDetails || [],
                asin: data.asin,
                lowest_resale_price: data.lowestResalePrice,
                pallet_order_id: data.palletOrderID,
                purchase_price: data.purchasePrice,
            });
        },
    });

    React.useEffect(() => {
        if (cloneId) {
            setLoadingClone(true);
            fetchToClone.mutate({id: cloneId});
            setLoadingClone(false);
        } else {
            form.setFieldValue('status', AdminProductStatus.ProductStatusReadyForSale);
        }
    }, []);

    const categoriesQuery = ApiQuery.useGetAdminProductCategoryListQuery({
        refetchOnMount: 'always',
        onSuccess(data) {
            const auction = data.items?.find((category) => category.name === 'Auction');
            if (auction) {
                form.setFieldValue('category', auction.id);
            }
        },
    });

    const palletOrderQuery = ApiQuery.useAdminGetAllPalletsQuery({
        refetchOnMount: 'always',
    });

    const productConsistencyDetails = ApiQuery.useGetAdminProductsConsistencyDetailsQuery({
        refetchOnMount: 'always',
    });

    const create = ApiQuery.usePostAdminProductCreateMutation({
        async onSuccess(data) {
            if (fileList.length === 0) {
                notify.openNotificationWithIcon('success', 'Product created');
                navigate(`/products/${data.id}`);
                return;
            }
            console.log(fileList);
            // make array of Blob
            try {
                const request: ProductsApiPostAdminAttachImagesRequest = {
                    id: data.id, // Укажите реальный ID продукта
                    images: fileList.map((file) => file.originFileObj as Blob),
                };

                await attachImages.mutateAsync(request);
                notify.openNotificationWithIcon('success', 'Product created and images attached');
                navigate(`/products/${data.id}`);
            } catch (error) {
                console.error('Error while attaching images', error);
                notify.openNotificationWithIcon('error', 'Error while attaching images');
                setCreateLoading(false);
            }
        },
        onError(error) {
            setCreateLoading(false);
            console.log(error);
            setError('Something went wrong');
        },
    });

    const attachImages = ApiQuery.usePostAdminAttachImagesMutation({});

    const handlePreview = async (file: UploadFile) => {
        console.log('preview', file);
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj as FileType);
        }

        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    };

    const handleRemoveImage = (file: UploadFile) => {
        const index = fileList.indexOf(file);
        const newFileList = fileList.slice();
        newFileList.splice(index, 1);
        setFileList(newFileList);
    };

    const handleAppendImage = (file: RcFile) => {
        console.log('append', file);
        return false;
    };

    const OnChange = (info: UploadChangeParam) => {
        console.log('info', info);
        setFileList(info.fileList);
    };

    const uploadButton = (
        <button
            style={{border: 0, background: 'none'}}
            type="button"
        >
            <PlusOutlined />
            <div style={{marginTop: 8}}>Upload</div>
        </button>
    );

    const [suggestedProducts, setSuggestedProducts] = React.useState<Array<AdminPalletOrderLookupItem>>([]);

    const lookupProductRequest = useAdminPalletOrderLookupMutation({
        onSuccess: (data) => {
            if (data.items.find((item) => item.name === form.getFieldValue('name'))) {
                setSuggestedProducts([]);
                return;
            }
            setSuggestedProducts(data.items);
        },
        onError: () => {
            notify.openNotificationWithIcon('error', 'Failed to fetch suggested products');
        },
    });

    const name = Form.useWatch('name', form);
    const palletOrderId = Form.useWatch('pallet_order_id', form);

    useEffect(() => {
        if (palletOrderId && name) {
            const delayDebounceFn = setTimeout(() => {
                lookupProductRequest.mutate({
                    id: palletOrderId,
                    name: name || '',
                });
            }, 500);

            return () => clearTimeout(delayDebounceFn);
        }
    }, [name]);

    const applySuggestions = (item: AdminPalletOrderLookupItem) => {
        form.setFieldsValue({
            name: item.name,
            asin: item.asin,
            purchase_price: item.price,
        });
        setSuggestedProducts([]);
    };

    const handleSubmit = () => {
        form.validateFields({validateOnly: true})
            .then(() => {
                const mps = marketplaces?.map((mp: Marketplace) => ({
                    name: mp.name,
                    link: mp.link,
                    price: mp.price,
                }));
                setError(null);
                setCreateLoading(true);
                create.mutate({
                    params: {
                        name: form.getFieldValue('name'),
                        description: form.getFieldValue('description') || 'description',
                        mSRP: form.getFieldValue('msrp'),
                        categoryID: form.getFieldValue('category'),
                        status: form.getFieldValue('status'),
                        palletOrderID: form.getFieldValue('pallet_order_id'),
                        marketplaces: form.getFieldValue('marketplaces'),
                        consistency: form.getFieldValue('consistency'),
                        consistencyDetails: form.getFieldValue('consistency_details'),
                        asin: form.getFieldValue('asin'),
                        lowestResalePrice: form.getFieldValue('lowest_resale_price'),
                        purchasePrice: form.getFieldValue('purchase_price'),
                    },
                });
            })
            .catch(() => {
                setError('Please fill all required fields');
            });
    };

    if (loadingClone) {
        return (
            <Spin
                spinning
                tip={'Fetching data...'}
            />
        );
    }

    return (
        <div>
            <h1>Create Product</h1>
            <Row gutter={16}>
                <Col
                    xs={{flex: '100%'}}
                    lg={{flex: '70%'}}
                    flex={'auto'}
                >
                    <Form
                        disabled={createLoading}
                        layout="horizontal"
                        labelCol={{span: 5}}
                        wrapperCol={{span: 19}}
                        form={form}
                    >
                        <Card
                            title={
                                <Flex
                                    align={'center'}
                                    justify={'space-between'}
                                >
                                    <h2>Main Info</h2>
                                </Flex>
                            }
                        >
                            <Form.Item
                                label="Pallet order"
                                name="pallet_order_id"
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="select pallet order"
                                    allowClear
                                    options={palletOrderQuery?.data?.items?.map((order) => ({
                                        label: `${dayjs(order.orderDate).format('MM/DD/YYYY hh:mm A')} - ${order.name}`,
                                        value: order.id,
                                    }))}
                                />
                            </Form.Item>
                            <div className={styles.productName}>
                                <Form.Item
                                    name="name"
                                    label="Name"
                                    rules={[{required: true, message: 'Please input name!'}]}
                                >
                                    <Input placeholder="input name" />
                                </Form.Item>
                                {suggestedProducts.length > 0 && (
                                    <List
                                        className={styles.suggestedProductsList}
                                        header={
                                            <Flex
                                                justify={'space-between'}
                                                align={'center'}
                                            >
                                                <div>Suggested products</div>
                                                <Button
                                                    size={'small'}
                                                    icon={<CloseOutlined />}
                                                    onClick={() => setSuggestedProducts([])}
                                                ></Button>
                                            </Flex>
                                        }
                                        bordered
                                        size={'small'}
                                        dataSource={suggestedProducts}
                                        renderItem={(item: AdminPalletOrderLookupItem) => (
                                            <List.Item
                                                actions={[
                                                    <Button onClick={() => applySuggestions(item)}>Apply</Button>,
                                                ]}
                                            >
                                                <div className={styles.suggestedProductsListItem}>
                                                    [{item.asin}
                                                    <Tooltip title={`Find Product: ${item.asin}`}>
                                                        <Button
                                                            size={'small'}
                                                            type="link"
                                                            onClick={() =>
                                                                window.open(
                                                                    `https://www.google.com/search?q=${item.asin}`,
                                                                    '_blank'
                                                                )
                                                            }
                                                            icon={<ExportOutlined />}
                                                            iconPosition={'end'}
                                                        />
                                                    </Tooltip>{' '}
                                                    ({item.quantity}/${item.price})] - {item.name}
                                                </div>
                                            </List.Item>
                                        )}
                                    />
                                )}
                            </div>

                            <Form.Item label={'ASIN'}>
                                <Space>
                                    <Form.Item
                                        name={'asin'}
                                        label={'input ASIN'}
                                        noStyle
                                    >
                                        <Input placeholder="input ASIN" />
                                    </Form.Item>
                                    <Tooltip title={`Find Product: ${asin}`}>
                                        <Button
                                            disabled={!asin}
                                            size={'small'}
                                            type="link"
                                            onClick={() =>
                                                window.open(`https://www.google.com/search?q=${asin}`, '_blank')
                                            }
                                            icon={<ExportOutlined />}
                                            iconPosition={'end'}
                                        />
                                    </Tooltip>
                                </Space>
                            </Form.Item>
                            <Form.Item
                                name="status"
                                label="Status"
                                rules={[{required: true, message: 'Please select status!'}]}
                            >
                                <Select
                                    optionFilterProp="label"
                                    placeholder="select statue"
                                    options={productStatuses.map((m) => ({
                                        label: m.label,
                                        value: m.key,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item
                                name="consistency"
                                label="Consistency"
                                rules={[{required: true, message: 'Please select consistency!'}]}
                            >
                                <Select
                                    optionFilterProp="label"
                                    placeholder="select consistency"
                                    options={productConsistencies.map((m) => ({
                                        label: m.label,
                                        value: m.key,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item
                                label={
                                    <Flex
                                        align={'center'}
                                        gap={5}
                                    >
                                        <span>Consistency Details</span>
                                        <Tooltip
                                            placement="right"
                                            title="Select from list or type and press Enter to add new"
                                        >
                                            <QuestionCircleFilled />
                                        </Tooltip>
                                    </Flex>
                                }
                                name="consistency_details"
                            >
                                <Select
                                    mode="tags"
                                    style={{width: '100%'}}
                                    placeholder="Select or type consistency details"
                                    options={productConsistencyDetails.data?.items?.map((item) => ({
                                        label: item,
                                        value: item,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item label="Purchase Price / MSRP / Lowest Price">
                                <Flex align={'center'}>
                                    <Space.Compact>
                                        <Form.Item
                                            noStyle
                                            name="purchase_price"
                                            rules={[{required: true, message: 'Purchase is required'}]}
                                        >
                                            <InputNumber
                                                prefix="$"
                                                min={0}
                                                max={100000}
                                                defaultValue={0}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            noStyle
                                            name="msrp"
                                            rules={[{required: true, message: 'MSRP is required'}]}
                                        >
                                            <InputNumber
                                                prefix="$"
                                                min={1}
                                                max={100000}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            noStyle
                                            name="lowest_resale_price"
                                            rules={[{required: true, message: 'Lowest Resale Price is required'}]}
                                        >
                                            <InputNumber
                                                prefix="$"
                                                min={0}
                                                max={100000}
                                                defaultValue={0}
                                            />
                                        </Form.Item>
                                    </Space.Compact>
                                </Flex>
                            </Form.Item>
                            <Form.Item
                                name="category"
                                label="Category"
                                rules={[{required: true, message: 'Please select category!'}]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="select category"
                                    options={categoriesQuery.data?.items?.map((category) => ({
                                        label: category.name,
                                        value: category.id,
                                    }))}
                                />
                            </Form.Item>

                            <Form.Item
                                label="Description"
                                name={'description'}
                            >
                                <Input.TextArea
                                    placeholder="input some description"
                                    rows={10}
                                />
                            </Form.Item>
                        </Card>
                        <Divider />
                        <h3>Marketplaces</h3>
                        <Form.List name="marketplaces">
                            {(fields, {add, remove}) => (
                                <div style={{display: 'flex', rowGap: 16, flexDirection: 'column'}}>
                                    {fields.map((field) => (
                                        <Card
                                            size="small"
                                            title={`Item ${field.name + 1}`}
                                            key={field.key}
                                            extra={
                                                <CloseOutlined
                                                    onClick={() => {
                                                        remove(field.name);
                                                    }}
                                                />
                                            }
                                        >
                                            <Form.Item
                                                label="Marketplace"
                                                name={[field.name, 'name']}
                                            >
                                                <Select
                                                    showSearch
                                                    optionFilterProp="label"
                                                    placeholder="select marketplace"
                                                    options={marketplaceLabels.map((m) => ({
                                                        label: m.label,
                                                        value: m.key,
                                                    }))}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label="Link"
                                                name={[field.name, 'link']}
                                            >
                                                <Input />
                                            </Form.Item>
                                            <Form.Item
                                                label="Price"
                                                name={[field.name, 'price']}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    max={100000}
                                                />
                                            </Form.Item>
                                        </Card>
                                    ))}

                                    <Button
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                    >
                                        + Add Marketplace
                                    </Button>
                                </div>
                            )}
                        </Form.List>

                        {error && <Text type={'danger'}>{error}</Text>}
                    </Form>
                </Col>
                <Col
                    xs={{flex: '100%'}}
                    lg={{flex: '30%'}}
                >
                    <Card
                        title={'Images'}
                        size={'small'}
                        style={{marginBottom: 16}}
                        loading={attachImages.isLoading}
                    >
                        {fileList.map((file) => file.fileName)}
                        <Upload
                            listType="picture-card"
                            fileList={fileList}
                            onPreview={handlePreview}
                            beforeUpload={handleAppendImage}
                            onRemove={handleRemoveImage}
                            onChange={OnChange}
                            multiple={true}
                        >
                            {fileList.length >= 8 ? null : uploadButton}
                        </Upload>
                        {previewImage && (
                            <Image
                                wrapperStyle={{display: 'none'}}
                                preview={{
                                    visible: previewOpen,
                                    onVisibleChange: (visible) => setPreviewOpen(visible),
                                    afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                }}
                                src={previewImage}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
            <Divider />
            <Flex
                justify={'end'}
                align={'center'}
                gap={16}
            >
                <Button onClick={() => navigate('/products')}>Cancel</Button>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={createLoading}
                    onClick={handleSubmit}
                >
                    Create
                </Button>
            </Flex>
        </div>
    );
};
